import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Receipt, Download, PaperPlaneTilt, CheckCircle, XCircle, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Phase 26 — admin-side invoicing block for a dispatch ride.
 *
 * Dedicated module — reads/writes ONLY `/api/admin/invoices` and the
 * ride-scoped creation endpoint. Does NOT touch driver_invoices, the driver
 * personal portal, or admin payments logic. Rendered inside the ride
 * detail modal so the admin can issue → download → send → mark-as-paid
 * without leaving the dispatch context.
 */

const STATUS_META = {
  draft:     { label: "Brouillon", className: "bg-zinc-700 text-zinc-200" },
  issued:    { label: "Émise",     className: "bg-sky-500/20 text-sky-300 border border-sky-500/30" },
  sent:      { label: "Envoyée",   className: "bg-violet-500/20 text-violet-300 border border-violet-500/30" },
  paid:      { label: "Payée",     className: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" },
  cancelled: { label: "Annulée",   className: "bg-red-500/20 text-red-300 border border-red-500/30" },
};

function fmtEur(n) {
  const v = Number(n || 0);
  return `${v.toFixed(2)} €`;
}

async function downloadPdf(invoice) {
  try {
    const res = await api.get(`/admin/invoices/${invoice.id}/pdf`, { responseType: "blob" });
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${invoice.invoice_number || "facture"}.pdf`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 500);
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Échec du téléchargement");
  }
}

export default function DispatchInvoiceSection({ ride, onInvoiceChanged }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    amount_ttc: "",
    vat_rate: "",
    client_name: "",
    client_email: "",
    client_address: "",
  });

  const fetchInvoices = useCallback(async () => {
    if (!ride?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get("/admin/invoices", { params: { ride_id: ride.id } });
      setInvoices(data || []);
    } catch {
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [ride?.id]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  // Prefill the form from the ride (client_name / email / price) when the
  // admin opens the creation panel — keeps typing to a minimum.
  useEffect(() => {
    if (!showCreate || !ride) return;
    setForm({
      amount_ttc: ride.client_price != null ? String(ride.client_price)
                  : ride.admin_price != null ? String(ride.admin_price) : "",
      vat_rate: "",    // server uses issuer's default when empty
      client_name: ride.client_name || "",
      client_email: ride.client_email || "",
      client_address: "",
    });
  }, [showCreate, ride]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const activeInvoice = invoices.find(i => i.status !== "cancelled") || null;

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount_ttc);
    if (!amt || amt <= 0) { toast.error("Montant TTC invalide"); return; }
    setCreating(true);
    try {
      const payload = {
        amount_ttc: amt,
        vat_rate: form.vat_rate !== "" ? parseFloat(form.vat_rate) : null,
        client_name: form.client_name || null,
        client_email: form.client_email || null,
        client_address: form.client_address || null,
      };
      await api.post(`/admin/rides/${ride.id}/invoice`, payload);
      toast.success("Facture créée");
      setShowCreate(false);
      fetchInvoices();
      onInvoiceChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la création");
    } finally {
      setCreating(false);
    }
  };

  const sendToClient = async (inv) => {
    if (!inv?.client_email) { toast.error("Aucun email client sur la facture"); return; }
    if (!window.confirm(`Envoyer la facture ${inv.invoice_number} à ${inv.client_email} ?`)) return;
    setSending(true);
    try {
      await api.post(`/admin/invoices/${inv.id}/send`);
      toast.success("Facture envoyée");
      fetchInvoices();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const setStatus = async (inv, status) => {
    const verb = { paid: "Marquer payée ?", cancelled: "Annuler la facture ?" }[status];
    if (verb && !window.confirm(verb)) return;
    try {
      await api.put(`/admin/invoices/${inv.id}/status`, { status });
      toast.success("Statut mis à jour");
      fetchInvoices();
      onInvoiceChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  return (
    <div data-testid="dispatch-invoice-section" className="bg-[#0f0f0f] border border-zinc-800 rounded-md p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Receipt size={16} className="text-amber-400" />
          <p className="text-sm font-medium text-white">Facturation client</p>
        </div>
        {!activeInvoice && !showCreate && (
          <Button data-testid="invoice-create-open" onClick={() => setShowCreate(true)}
            className="bg-amber-500 text-black hover:bg-amber-600 font-semibold h-8 text-xs">
            <Plus size={14} className="mr-1" /> Générer la facture
          </Button>
        )}
      </div>

      {loading && <p className="text-xs text-zinc-500">Chargement…</p>}

      {!loading && invoices.map((inv) => {
        const meta = STATUS_META[inv.status] || STATUS_META.issued;
        return (
          <div key={inv.id} data-testid={`invoice-card-${inv.id}`}
            className="bg-[#121212] border border-zinc-800 rounded-md p-3 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs font-mono text-amber-300">{inv.invoice_number}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${meta.className}`}>{meta.label}</span>
              </div>
              <span className="text-xs font-semibold text-white">{fmtEur(inv.amount_ttc)}</span>
            </div>
            <div className="text-[11px] text-zinc-500 space-y-0.5">
              <p>Client : <span className="text-zinc-300">{inv.client_name}</span>{inv.client_email ? ` · ${inv.client_email}` : ""}</p>
              <p>
                HT {fmtEur(inv.amount_ht)} · TVA {inv.vat_rate}% ({fmtEur(inv.vat_amount)})
                · Échéance {inv.due_date || "—"}
              </p>
              {inv.sent_at && <p className="text-violet-400">Envoyée le {new Date(inv.sent_at).toLocaleString("fr-FR")}</p>}
              {inv.paid_at && <p className="text-emerald-400">Payée le {new Date(inv.paid_at).toLocaleString("fr-FR")}</p>}
            </div>
            {inv.status !== "cancelled" && (
              <div className="flex gap-1.5 flex-wrap pt-1">
                <Button data-testid={`invoice-download-${inv.id}`} onClick={() => downloadPdf(inv)}
                  variant="outline" className="h-8 text-xs border-amber-500/30 text-amber-200 hover:bg-amber-500/10">
                  <Download size={12} className="mr-1" /> PDF
                </Button>
                <Button data-testid={`invoice-send-${inv.id}`} onClick={() => sendToClient(inv)} disabled={sending || !inv.client_email}
                  variant="outline" className="h-8 text-xs border-violet-500/30 text-violet-200 hover:bg-violet-500/10">
                  <PaperPlaneTilt size={12} className="mr-1" /> Envoyer
                </Button>
                {inv.status !== "paid" && (
                  <Button data-testid={`invoice-paid-${inv.id}`} onClick={() => setStatus(inv, "paid")}
                    variant="outline" className="h-8 text-xs border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/10">
                    <CheckCircle size={12} className="mr-1" /> Marquer payée
                  </Button>
                )}
                <Button data-testid={`invoice-cancel-${inv.id}`} onClick={() => setStatus(inv, "cancelled")}
                  variant="outline" className="h-8 text-xs border-red-500/30 text-red-300 hover:bg-red-500/10">
                  <XCircle size={12} className="mr-1" /> Annuler
                </Button>
              </div>
            )}
          </div>
        );
      })}

      {showCreate && (
        <form onSubmit={submit} data-testid="invoice-create-form" className="space-y-3 border border-amber-500/30 bg-amber-500/5 rounded-md p-3">
          <p className="text-xs font-medium text-amber-200">Nouvelle facture — {ride?.route_label || ""}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Montant TTC *</Label>
              <Input data-testid="invoice-amount-input" type="number" step="0.01" inputMode="decimal"
                required value={form.amount_ttc} onChange={set("amount_ttc")}
                className="bg-[#1A1A1A] border-zinc-700 text-white focus:border-amber-500 h-10" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">TVA (%)</Label>
              <select data-testid="invoice-vat-select" value={form.vat_rate} onChange={set("vat_rate")}
                className="w-full bg-[#1A1A1A] border border-zinc-700 text-white h-10 px-3 rounded-md focus:border-amber-500 focus:outline-none text-sm">
                <option value="">Auto (selon profil société)</option>
                <option value="0">0 % (non assujetti)</option>
                <option value="10">10 % (transport de personnes)</option>
                <option value="20">20 % (prestation standard)</option>
              </select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Nom du client *</Label>
            <Input data-testid="invoice-client-name-input" required value={form.client_name} onChange={set("client_name")}
              className="bg-[#1A1A1A] border-zinc-700 text-white focus:border-amber-500 h-10" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Email client</Label>
            <Input data-testid="invoice-client-email-input" type="email" value={form.client_email} onChange={set("client_email")}
              placeholder="client@exemple.fr"
              className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Adresse client</Label>
            <Input data-testid="invoice-client-address-input" value={form.client_address} onChange={set("client_address")}
              placeholder="(optionnel — pour la facture)"
              className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" />
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={() => setShowCreate(false)}
              className="border border-zinc-700 text-zinc-300 hover:bg-zinc-800 flex-1">Annuler</Button>
            <Button type="submit" data-testid="invoice-create-submit" disabled={creating}
              className="bg-amber-500 text-black hover:bg-amber-600 font-semibold flex-1">
              {creating ? "..." : "Créer la facture"}
            </Button>
          </div>
        </form>
      )}

      {!loading && invoices.length === 0 && !showCreate && (
        <p data-testid="invoice-empty" className="text-xs text-zinc-500 italic">Aucune facture pour cette course.</p>
      )}
    </div>
  );
}
